"use client";

import { useEffect, useMemo } from "react";

import { MODELS } from "../constants";
import SingleSelect from "../SingleSelect";

const ASPECT_RATIOS = [
  { ratio: "1:1", x: 8, y: 8, width: 16, height: 16, premiumOnly: false },
  { ratio: "2:3", x: 11, y: 8.5, width: 10, height: 15, premiumOnly: false },
  { ratio: "9:16", x: 12, y: 4, width: 8, height: 24, premiumOnly: true },
  { ratio: "16:9", x: 4, y: 12, width: 24, height: 8, premiumOnly: true },
  { ratio: "3:4", x: 10, y: 6, width: 12, height: 20, premiumOnly: true },
  { ratio: "4:3", x: 6, y: 10, width: 20, height: 12, premiumOnly: true },
  { ratio: "3:2", x: 5.5, y: 9, width: 21, height: 14, premiumOnly: true },
  { ratio: "21:9", x: 5, y: 13, width: 22, height: 6, premiumOnly: true },
];

const AspectRatioIcon = ({
  x,
  y,
  width,
  height,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
}) => {
  return (
    <div className="tw:w-4 tw:h-4 tw:text-primary-red tw:flex-shrink-0">
      <svg
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x={x} y={y} width={width} height={height} rx="2" />
      </svg>
    </div>
  );
};

interface AspectRatioSelectProps {
  selectedModel: string;
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  isSubscribed: boolean;
}

const AspectRatioSelect: React.FC<AspectRatioSelectProps> = ({
  selectedModel,
  aspectRatio,
  setAspectRatio,
  isSubscribed,
}) => {
  const model = MODELS.find((m) => m.name === selectedModel) || MODELS[0];

  const selected = useMemo(() => {
    const selectedAspectRatio = ASPECT_RATIOS.find(
      (r) => r.ratio === aspectRatio
    );

    return selectedAspectRatio ? selectedAspectRatio : ASPECT_RATIOS[0];
  }, [aspectRatio]);

  useEffect(() => {
    if (!model.includedAspectRatios.includes(aspectRatio)) {
      setAspectRatio("1:1");
    }
  }, [selectedModel]);

  return (
    <SingleSelect
      selected={{
        value: aspectRatio,
        label: `${aspectRatio} Ratio`,
        icon: (
          <AspectRatioIcon
            x={selected.x}
            y={selected.y}
            width={selected.width}
            height={selected.height}
          />
        ),
      }}
      options={ASPECT_RATIOS.map((r) => ({
        value: r.ratio,
        label: `${r.ratio} Ratio`,
        disabled: !model.includedAspectRatios.includes(r.ratio),
        locked: r.premiumOnly && !isSubscribed,
        icon: (
          <AspectRatioIcon x={r.x} y={r.y} width={r.width} height={r.height} />
        ),
      }))}
      onSelect={setAspectRatio}
      label="Ratio"
      className="tw:w-[108px]"
    />
  );
};

export default AspectRatioSelect;
